import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Coffee, Square, Plus, Trash2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel, Tag, Initials, attTone } from "@/components/kit";
import { Field } from "@/components/Forms";
import { ConfirmDialog, DIALOG_CLS } from "@/components/Dialogs";
import { useStore } from "@/store/AppStore";
import { teamOf, ranking, nameMap } from "@/lib/selectors";
import { ABSENCE, DAY_STATE, PUNCH_LABEL, nowHM, fmtMin, entryOf, absenceOn, dayState, worked, paused, timeOf, firstStart, monthReport, SHIFT_TOLERANCE } from "@/lib/attendance";
import { TODAY, MONTH_LABEL } from "@/data/mock";
import { fmtDate } from "@/lib/format";

const NEXT = {
  nao_iniciou: [["inicio", "Iniciar jornada", Play]],
  trabalhando: [["pausa", "Pausar", Coffee], ["termino", "Encerrar jornada", Square]],
  pausa: [["retorno", "Retornar da pausa", Play]],
  encerrada: [],
};

const useTick = () => {
  const [, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
};

export function JourneyCard({ collab }) {
  const { state, punch } = useStore();
  useTick();
  const entry = entryOf(state, collab.id, TODAY);
  const st = dayState(entry);
  const absence = absenceOn(state, collab.id, TODAY);
  const doPunch = (type) => {
    punch(collab, type);
    toast.success(`${PUNCH_LABEL[type]} registrado às ${nowHM()}.`);
  };
  return (
    <Panel title="Jornada de hoje" subtitle={fmtDate(TODAY)} testId="journey-card" action={<Tag tone={DAY_STATE[st].tone} testId="journey-state">{DAY_STATE[st].label}</Tag>}>
      {absence && <p className="mb-4 rounded-lg bg-gold-soft p-3 text-sm text-gold-dark" data-testid="journey-absence">Hoje está registrado: {ABSENCE[absence.type].label}.</p>}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-neutral-400">Horas trabalhadas</p>
          <p className="font-display text-3xl font-semibold" data-testid="journey-worked">{fmtMin(worked(entry))}</p>
          {entry && <p className="mt-1 text-xs text-neutral-500">Pausas: {fmtMin(paused(entry))}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {NEXT[st].map(([type, label, Icon]) => (
            <Button key={type} variant={type === "termino" ? "outline" : "default"} className="font-semibold" onClick={() => doPunch(type)} data-testid={`journey-${type}-button`}>
              <Icon className="mr-1.5 h-4 w-4" /> {label}
            </Button>
          ))}
        </div>
      </div>
      {entry && (
        <ol className="mt-5 flex flex-wrap gap-2" data-testid="journey-events">
          {entry.events.map((e, i) => <li key={`${i}-${e.type}-${e.time}`} className="rounded-full border border-neutral-200 px-3 py-1 text-xs"><span className="text-neutral-500">{PUNCH_LABEL[e.type]}</span> <span className="font-semibold">{e.time}</span></li>)}
        </ol>
      )}
    </Panel>
  );
}

const statusOf = (s, c) => {
  const a = absenceOn(s, c.id, TODAY);
  return a ? ABSENCE[a.type] : DAY_STATE[dayState(entryOf(s, c.id, TODAY))];
};

export function TodayTable({ companyId, canMark }) {
  const { state, saveAbsence } = useStore();
  useTick();
  const mark = (c) => {
    saveAbsence({ companyId, collaboratorId: c.id, type: "falta", start: TODAY, end: TODAY, note: "Marcada pela gestora" });
    toast.success(`Falta registrada para ${c.name}.`);
  };
  return (
    <Panel title="Presença de hoje" subtitle={`${fmtDate(TODAY)} · tolerância de entrada até ${SHIFT_TOLERANCE}`} testId="today-table">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-neutral-400">{["Colaboradora", "Situação", "Início", "Pausas", "Término", "Horas", ""].map((h) => <th key={h} className="pb-3 font-medium">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-neutral-100">
            {teamOf(state, companyId).map((c) => {
              const e = entryOf(state, c.id, TODAY);
              const st = statusOf(state, c);
              const late = (firstStart(e) || "00:00") > SHIFT_TOLERANCE;
              return (
                <tr key={c.id} data-testid={`today-row-${c.id}`}>
                  <td className="py-3"><div className="flex items-center gap-3"><Initials name={c.name} /><span className="font-medium">{c.name}</span></div></td>
                  <td><Tag tone={st.tone} testId={`today-status-${c.id}`}>{st.label}</Tag></td>
                  <td className={late ? "font-semibold text-red-600" : ""}>{firstStart(e) || "—"}{late && " (atraso)"}</td>
                  <td>{e ? fmtMin(paused(e)) : "—"}</td>
                  <td>{timeOf(e, "termino") || "—"}</td>
                  <td className="font-semibold">{e ? fmtMin(worked(e)) : "—"}</td>
                  <td className="text-right">{canMark && !e && !absenceOn(state, c.id, TODAY) && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => mark(c)} data-testid={`mark-absence-${c.id}`}>Marcar falta</Button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function AbsenceForm({ people, onSave }) {
  const [f, setF] = useState({ collaboratorId: people[0]?.id, type: "falta", start: TODAY, end: TODAY, note: "" });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    if (!f.collaboratorId) return toast.error("Selecione a colaboradora.");
    if (!f.start || !f.end || f.end < f.start) return toast.error("Confira as datas de início e fim.");
    onSave(f);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="absence-form">
      <Field label="Colaboradora">
        <Select value={f.collaboratorId} onValueChange={set("collaboratorId")}><SelectTrigger data-testid="absence-collab-select"><SelectValue /></SelectTrigger><SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
      </Field>
      <Field label="Tipo">
        <Select value={f.type} onValueChange={set("type")}><SelectTrigger data-testid="absence-type-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ABSENCE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Início"><Input type="date" value={f.start} onChange={set("start")} data-testid="absence-start-input" /></Field>
        <Field label="Fim"><Input type="date" value={f.end} onChange={set("end")} data-testid="absence-end-input" /></Field>
      </div>
      <Field label="Observação"><Input value={f.note} onChange={set("note")} placeholder="Ex.: atestado entregue" data-testid="absence-note-input" /></Field>
      <Button type="submit" className="w-full font-semibold" data-testid="absence-save-button">Registrar</Button>
    </form>
  );
}

export function AbsenceManager({ companyId }) {
  const { state, saveAbsence, deleteAbsence } = useStore();
  const [dlg, setDlg] = useState(null);
  const names = nameMap(state);
  const list = state.absences.filter((a) => a.companyId === companyId).sort((a, b) => b.start.localeCompare(a.start));
  return (
    <Panel title="Faltas, férias e afastamentos" subtitle={`${list.length} registro(s)`} testId="absence-manager" action={<Button className="h-9 font-semibold" onClick={() => setDlg({ type: "new" })} data-testid="new-absence-button"><Plus className="mr-1 h-4 w-4" /> Registrar ausência</Button>}>
      {!list.length && <p className="text-sm text-neutral-400">Nenhuma ausência registrada.</p>}
      <ul className="divide-y divide-neutral-100">
        {list.map((a) => (
          <li key={a.id} data-testid={`absence-row-${a.id}`} className="flex flex-wrap items-center gap-3 py-3.5">
            <Initials name={names[a.collaboratorId] || "?"} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">{names[a.collaboratorId]}</p>
              <p className="text-xs text-neutral-500">{a.start === a.end ? fmtDate(a.start) : `${fmtDate(a.start)} a ${fmtDate(a.end)}`}{a.note && ` · ${a.note}`}</p>
            </div>
            <Tag tone={ABSENCE[a.type].tone}>{ABSENCE[a.type].label}</Tag>
            {a.start > TODAY && <Tag>Programada</Tag>}
            <Button variant="ghost" size="icon" aria-label="Excluir" data-testid={`absence-delete-${a.id}`} onClick={() => setDlg({ type: "del", a })}><Trash2 className="h-4 w-4 text-neutral-500" /></Button>
          </li>
        ))}
      </ul>
      <Dialog open={dlg?.type === "new"} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className={DIALOG_CLS} data-testid="absence-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Registrar ausência</DialogTitle></DialogHeader>
          {dlg?.type === "new" && <AbsenceForm people={teamOf(state, companyId)} onSave={(f) => { saveAbsence({ ...f, companyId }); toast.success("Ausência registrada."); setDlg(null); }} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={dlg?.type === "del"} onOpenChange={(v) => !v && setDlg(null)} title="Excluir registro?" description={dlg?.a ? `${ABSENCE[dlg.a.type].label} de ${names[dlg.a.collaboratorId]}` : ""} testId="absence-delete-dialog" onConfirm={() => { deleteAbsence(dlg.a.id); toast.success("Registro excluído."); }} />
    </Panel>
  );
}

export function MonthReport({ companyId, title, hidePrint }) {
  const { state } = useStore();
  const rows = ranking(state, companyId).map((r) => ({ ...r, rep: monthReport(state, r) }));
  const cols = ["Colaboradora", "Dias trab.", "Horas", "Atrasos", "Faltas", "Férias", "Atestado", "Folga", "Tarefas", "Meta vendas", "Ranking"];
  return (
    <Panel title={title || `Relatório de desempenho · ${MONTH_LABEL}`} subtitle="Presença, tarefas e metas de cada colaboradora" testId={`month-report${hidePrint ? `-${companyId}` : ""}`} action={!hidePrint && <Button variant="outline" className="h-9 print:hidden" onClick={() => window.print()} data-testid="print-report-button"><Printer className="mr-1.5 h-4 w-4" /> Imprimir</Button>}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px] text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-neutral-400">{cols.map((h) => <th key={h} className="pb-3 pr-3 font-medium">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`report-row-${r.id}`}>
                <td className="py-3 pr-3"><p className="font-semibold">{r.name}</p><p className="text-xs text-neutral-500">{r.role}</p></td>
                <td className="pr-3">{r.rep.days}</td>
                <td className="pr-3 font-semibold">{fmtMin(r.rep.minutes)}</td>
                <td className={`pr-3 ${r.rep.late ? "font-semibold text-red-600" : ""}`}>{r.rep.late}</td>
                <td className={`pr-3 ${r.rep.falta ? "font-semibold text-red-600" : ""}`} data-testid={`report-falta-${r.id}`}>{r.rep.falta}</td>
                <td className="pr-3" data-testid={`report-ferias-${r.id}`}>{r.rep.ferias}</td>
                <td className="pr-3">{r.rep.atestado}</td>
                <td className="pr-3">{r.rep.folga}</td>
                <td className="pr-3">{r.tasks}%</td>
                <td className="pr-3"><Tag tone={attTone(r.att)}>{r.att}%</Tag></td>
                <td>{r.position}º</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="py-4 text-sm text-neutral-400" data-testid="month-report-empty">Nenhuma colaboradora cadastrada.</p>}
      </div>
    </Panel>
  );
}

export function PresenceSummary({ companyId }) {
  const { state } = useStore();
  useTick();
  const team = teamOf(state, companyId);
  return (
    <Panel title="Presença hoje" subtitle="Quem está trabalhando, em pausa ou ausente" className="lg:col-span-3" testId="presence-summary">
      <div className="flex flex-wrap gap-3">
        {team.map((c) => {
          const st = statusOf(state, c);
          return (
            <div key={c.id} data-testid={`presence-${c.id}`} className="flex items-center gap-2 rounded-full border border-neutral-200 py-1 pl-1 pr-3">
              <Initials name={c.name} className="h-7 w-7 text-[10px]" />
              <span className="text-xs font-medium">{c.name.split(" ")[0]}</span>
              <Tag tone={st.tone}>{st.label}</Tag>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
